import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

const NAV_LINKS = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "专题", to: "/special" },
  { label: "关于", to: "/about" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword("");
    setSearchOpen(false);
  };

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to.split("?")[0]);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled ? "bg-background-50/95 backdrop-blur border-b border-background-200" : "bg-background-50"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 md:h-20 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer">
            <span className="w-9 h-9 md:w-10 md:h-10 rounded-md bg-primary-500 text-accent-400 flex items-center justify-center">
              <i className="ri-film-line text-xl"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading font-black text-lg md:text-xl text-foreground-950 tracking-tight">
                生活中的玛丽
              </span>
              <span className="text-[10px] md:text-[11px] text-foreground-500 tracking-[0.22em] mt-0.5">
                FILM &amp; TV
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 lg:gap-2">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  isActive(link.to)
                    ? "text-primary-500 bg-primary-50"
                    : "text-foreground-700 hover:text-primary-500 hover:bg-background-100"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <form onSubmit={submitSearch} className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视作品"
                className="w-44 lg:w-60 h-10 pl-9 pr-3 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 focus:bg-background-50 transition-colors"
              />
            </form>
            <Link
              to="/special"
              className="h-10 px-4 rounded-full bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium whitespace-nowrap flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <i className="ri-bookmark-3-line"></i>
              专题精选
            </Link>
          </div>

          <div className="flex md:hidden items-center gap-1">
            <button
              type="button"
              onClick={() => setSearchOpen((v) => !v)}
              aria-label="搜索"
              className="w-10 h-10 rounded-md flex items-center justify-center text-foreground-700 hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-search-line text-xl"></i>
            </button>
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="菜单"
              className="w-10 h-10 rounded-md flex items-center justify-center text-foreground-700 hover:bg-background-100 cursor-pointer"
            >
              <i className={`text-2xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>

        {searchOpen && (
          <form onSubmit={submitSearch} className="md:hidden pb-4 animate-fade-in-fast">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视作品"
                autoFocus
                className="w-full h-11 pl-9 pr-3 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300"
              />
            </div>
          </form>
        )}
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 animate-fade-in-fast">
          <nav className="px-4 py-3 flex flex-col">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3 py-3 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                  isActive(link.to)
                    ? "text-primary-500 bg-primary-50"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/special"
              className="mt-2 px-3 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-medium text-center whitespace-nowrap cursor-pointer"
            >
              专题精选
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}